import { useMemo, useState } from "react"
import { Search } from "@mui/icons-material"
import {
    Box,
    Divider,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { StandardButton } from "../../buttons/StandardButton"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { SelectionDialog } from "../../../../modules/company/admin/organization/userGroups/components/Elements"

import { BillReceiverListItemVM } from "../billReceiverTypes"
import type { AddBillReceiverDialogCoreProps } from "./dialogTypes"

const useStyles = makeStyles()((theme) => ({
    infoText: {
        color: theme.palette.tailwind.slate[300],
    },
    orText: {
        color: theme.palette.tailwind.slate[300],
        margin: theme.spacing(0, 2),
        textTransform: "lowercase",
    },
    listItem: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "0.25rem",
        color: theme.palette.tailwind.slate[600],
        padding: "1rem",
        marginBottom: "0.75rem",
        cursor: "pointer",
        "&:last-child": {
            marginBottom: 0,
        },
    },
    listItemNumbers: {
        display: "flex",
        gap: "1rem",
    },
    dividerContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: theme.spacing(1, 0),
    },
    divider: {
        width: 80,
    },
    createButton: {
        width: "fit-content",
        alignSelf: "center",
    },
}))

export function AddBillReceiverDialogCore({
    type,
    searchText = "",
    items,
    dialogState,
    setDialogState,
    onChooseBillReceiver,
    onCreateNewBill,
}: AddBillReceiverDialogCoreProps) {
    const { classes } = useStyles()

    const [search, setSearch] = useState(searchText)
    const [selectedItemId, setSelectedItemId] = useState<string | undefined>()
    const searchRegex = useMemo(() => (search ? new RegExp(`(${search})`, "i") : undefined), [search])

    const filteredItems = useMemo(() => {
        if (!searchRegex) return items
        return items.filter(
            (receiver) =>
                searchRegex.test(receiver.name) ||
                (receiver.cvr && searchRegex.test(receiver.cvr)) ||
                searchRegex.test(receiver.endpoint.value) ||
                (receiver.address ? searchRegex.test(receiver.address) : false),
        )
    }, [items, searchRegex])

    const findReceiverById = (id: string, cb: (receiver: BillReceiverListItemVM) => void) => {
        const selectedReceiver = filteredItems.find((receiver) => receiver.id === id)
        if (selectedReceiver) cb(selectedReceiver)
    }

    const handleChange = (event: SelectChangeEvent) => {
        setSelectedItemId(event.target.value)
        findReceiverById(event.target.value, onChooseBillReceiver)
    }

    const onChoose = () => selectedItemId && findReceiverById(selectedItemId, onChooseBillReceiver)

    const modalTitleId =
        type === "new"
            ? "create-or-edit-company-user-group-connected-bill-receiver-modal-add-bill-receiver"
            : "create-or-edit-company-user-group-connected-bill-receiver-modal-update-bill-receiver"

    return (
        <LocalizedStrict id={modalTitleId} attrs={{ title: type === "new" }}>
            <SelectionDialog
                dialogState={dialogState}
                setDialogState={setDialogState}
                title={type === "new" ? "Add bill receiver" : ""}
            >
                <Grid container gap={3} flexDirection="column">
                    {type === "new" && (
                        <LocalizedStrict
                            id="create-or-edit-company-user-group-connected-bill-receiver-modal-field-search"
                            attrs={{ label: true, placeholder: true }}
                        >
                            <TextField
                                name="search"
                                value={search}
                                fullWidth
                                label="Search"
                                placeholder="Search by name, CVR, EAN, email or address"
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </LocalizedStrict>
                    )}

                    {type === "update" && (
                        <LocalizedStrict
                            id="create-or-edit-company-user-group-connected-bill-receiver-modal-bill-found"
                            vars={{ count: filteredItems.length }}
                        >
                            <Typography variant="h5" textAlign="center" fontWeight={700}>
                                {filteredItems.length} bill receivers already exists with the same identifier.
                            </Typography>
                        </LocalizedStrict>
                    )}

                    {search ? (
                        filteredItems.length ? (
                            <>
                                <LocalizedStrict id="bill-receivers-create-dialog-select-bill-receiver">
                                    <Typography variant="subtitle1" textAlign="start">
                                        Select existing bill receiver:
                                    </Typography>
                                </LocalizedStrict>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel id="bill-receivers-select-list">
                                        <LocalizedStrict id="bill-receivers-select-list-label">
                                            <>Select bill receiver</>
                                        </LocalizedStrict>
                                    </InputLabel>
                                    <Select
                                        labelId="bill-receivers-select-list"
                                        id="bill-receivers-select-label"
                                        value={selectedItemId ?? ""}
                                        onChange={handleChange}
                                        label="Select bill receiver"
                                        placeholder="Select bill receiver"
                                    >
                                        {filteredItems.map((receiver) => (
                                            <MenuItem
                                                key={receiver.id}
                                                value={receiver.id}
                                                className={classes.listItem}
                                            >
                                                <Typography variant="subtitle2">{receiver.name}</Typography>
                                                <Box className={classes.listItemNumbers}>
                                                    {receiver.cvr && (
                                                        <Typography variant="body2">
                                                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-cvr">
                                                                <>CVR:</>
                                                            </LocalizedStrict>{" "}
                                                            {receiver.cvr}
                                                        </Typography>
                                                    )}

                                                    {receiver.endpoint.kind === "ean" ? (
                                                        <Typography variant="body2">
                                                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-ean">
                                                                <>EAN:</>
                                                            </LocalizedStrict>{" "}
                                                            {receiver.endpoint.value}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2">
                                                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-email">
                                                                <>Email:</>
                                                            </LocalizedStrict>{" "}
                                                            {receiver.endpoint.value}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {receiver.address && (
                                                    <Typography variant="body2">
                                                        <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-address">
                                                            <>Address:</>
                                                        </LocalizedStrict>{" "}
                                                        {receiver.address}
                                                    </Typography>
                                                )}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        ) : (
                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-empty">
                                <Typography
                                    className={classes.infoText}
                                    variant="body2"
                                    textAlign="center"
                                    fontStyle="italic"
                                >
                                    No search results found
                                </Typography>
                            </LocalizedStrict>
                        )
                    ) : (
                        <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-info-text">
                            <Typography
                                className={classes.infoText}
                                variant="body2"
                                textAlign="center"
                                fontStyle="italic"
                            >
                                Start typing to show search results
                            </Typography>
                        </LocalizedStrict>
                    )}

                    <Grid container gap={2} flexDirection="column">
                        {type === "new" && (
                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-submit-button">
                                <StandardButton onClick={onChoose} disabled={!selectedItemId}>
                                    Choose selected bill receiver
                                </StandardButton>
                            </LocalizedStrict>
                        )}
                        <Box className={classes.dividerContainer}>
                            <Divider className={classes.divider} />
                            <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-or">
                                <Typography className={classes.orText} variant="body2" textAlign="center">
                                    or
                                </Typography>
                            </LocalizedStrict>
                            <Divider className={classes.divider} />
                        </Box>
                        <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-create-button">
                            <StandardButton
                                color="blue"
                                variant="outlined"
                                onClick={onCreateNewBill}
                                updateDefaultClasses={classes.createButton}
                            >
                                Create new bill receiver
                            </StandardButton>
                        </LocalizedStrict>
                        <LocalizedStrict id="create-or-edit-company-user-group-connected-bill-receiver-modal-info-warning">
                            <Typography variant="body2" className={classes.infoText} textAlign="center">
                                You should create a new bill receiver with the same identifier (CVR, EAN, email) only if
                                the contact details differ. For example, this may be necessary if the bill needs to be
                                directed to a different individual.
                            </Typography>
                        </LocalizedStrict>
                    </Grid>
                </Grid>
            </SelectionDialog>
        </LocalizedStrict>
    )
}
